import React, { useState } from 'react';

import { useMutation } from '@apollo/react-hooks';
import { TextField } from '@material-ui/core';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useTranslation } from 'react-i18next';

import { CONTACT_US } from '../../graphql/contact';
import { ContactUsDepartment } from '../../types';
import { CustomButton } from '../UI/CustomButton';
import useStyles from './styles';

const triggerSubmitEvent = () => {
  if (!document) return;

  let event;
  if (document.createEvent) {
    event = document.createEvent('HTMLEvents');
    event.initEvent('submit', true, true);
    event.eventName = 'submit';
    document.dispatchEvent(event);
  } else {
    // @ts-ignore
    event = document.createEventObject();
    event.eventName = 'submit';
    event.eventType = 'submit';
    // @ts-ignore
    document.fireEvent('on' + event.eventType, event);
  }
};

function Alert(props: AlertProps) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

export const ContactForm = ({ background, departments }) => {
  const classes = useStyles(background)();
  const { t } = useTranslation();

  const [email, setEmail] = useState<string | undefined>('');
  const [emailErr, setEmailErr] = useState<string | null>(null);
  const [department, setDepartment] = useState<ContactUsDepartment | null | undefined>(null);
  const [depErr, setDepErr] = useState<string | null>(null);
  const [details, setDetails] = useState<string | undefined>('');
  const [open, setOpen] = React.useState(false);

  const [sendForm] = useMutation(CONTACT_US);

  const showAlert = () => {
    setOpen(true);
  };

  const hideAlert = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);
  };

  const validateEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const validateForm = () => {
    if (!email) {
      setEmailErr(t('error.fieldRequired'));
    }

    if (email && !validateEmail(email)) {
      setEmailErr(t('error.emailIncorrect'));
    }

    if (!department) {
      setDepErr(t('error.fieldRequired'));
    }

    return !(!email || (email && !validateEmail(email)) || !department);
  };

  const send = async () => {
    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    await sendForm({ variables: { input: { email, department: department?.name, details } } });

    setEmail('');
    setDepartment(null);
    setDetails('');

    showAlert();
    triggerSubmitEvent();
  };

  return (
    <>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className={classes.contactForm}
      >
        <div className={classes.contactFormFieldWrap}>
          <TextField
            className={classes.contactFormField}
            name="email"
            label={t('input.placeholder.email')}
            variant="outlined"
            value={email}
            onChange={(event) => {
              setEmailErr(null);
              setEmail(event.target.value);
            }}
            error={!!emailErr}
            helperText={emailErr}
          />
        </div>

        <div className={classes.contactFormFieldWrap}>
          <Autocomplete
            options={departments}
            getOptionLabel={(option) => option?.name || ''}
            value={department}
            onChange={(event, value) => {
              if (value) {
                setDepErr(null);
              }
              setDepartment(value);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                name="department"
                label={t('input.placeholder.department')}
                variant="outlined"
                error={!!depErr}
                helperText={depErr}
              />
            )}
          />
        </div>

        <TextField
          name="details"
          className={classes.contactFormField}
          label={t('input.placeholder.details')}
          variant="outlined"
          multiline
          rows={9}
          value={details}
          onChange={(event) => {
            setDetails(event.target.value);
          }}
        />

        <CustomButton type="submit" btnClass={classes.contactFormButton}>
          {t('button.send')}
        </CustomButton>
      </form>
      <Snackbar open={open} autoHideDuration={3000} onClose={hideAlert}>
        <Alert onClose={hideAlert} severity="success">
          {t('alert.willContactYou')}
        </Alert>
      </Snackbar>
    </>
  );
};
